/** Motion follows the theme switch and device preference; shopping never depends on animation. */
export const createBoosterMotion = (document) => {
    const win = document.defaultView;
    const reduced = win.matchMedia('(prefers-reduced-motion: reduce)');
    const seen = new Set();
    const animations = new Map();
    const images = new WeakMap();
    const ease = win.getComputedStyle(document.body).getPropertyValue('--ov-motion-ease').trim() || 'cubic-bezier(.22, 1, .36, 1)';
    const enabled = () => document.body.classList.contains('ovalia-motion') && !reduced.matches && !document.hidden;
    const visible = (element) => {
        const rect = element.getBoundingClientRect();
        const rail = element.closest('[data-cross-sell-rail]')?.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < win.innerHeight && rect.right > (rail?.left ?? 0) && rect.left < (rail?.right ?? win.innerWidth);
    };
    const play = async (element, frames, duration = 460, delay = 0) => {
        animations.get(element)?.cancel();
        if (!element?.animate || !enabled() || !element.isConnected) return;
        let animation;
        try {
            animation = element.animate(frames, { duration, delay, easing: ease, fill: 'both' });
            animations.set(element, animation);
            await animation.finished;
        } catch (_) { /* Cancellation or unsupported effects keep the native visible state. */ }
        finally {
            if (animations.get(element) === animation) animations.delete(element);
            animation?.cancel();
        }
    };
    const enter = (element, index = 0) => {
        seen.add(element.dataset.boosterOffer);
        return play(element, [{ opacity: 0, transform: 'translate3d(0, 14px, 0)' }, { opacity: 1, transform: 'translate3d(0, 0, 0)' }], 540, index * 55);
    };
    const observer = win.IntersectionObserver ? new win.IntersectionObserver((entries) => {
        let index = 0;
        entries.forEach(({ target, isIntersecting }) => {
            if (!isIntersecting) return;
            observer.unobserve(target);
            if (!seen.has(target.dataset.boosterOffer)) enter(target, Math.min(index++, 2));
        });
    }, { threshold: .08 }) : null;
    const stop = () => animations.forEach((animation) => animation.cancel());
    const onPreference = () => { if (reduced.matches) stop(); };
    const onVisibility = () => { if (document.hidden) stop(); };
    const onFocus = ({ target }) => {
        const card = target.closest?.('[data-booster-offer]');
        if (card) { seen.add(card.dataset.boosterOffer); observer?.unobserve(card); animations.get(card)?.cancel(); }
    };
    reduced.addEventListener('change', onPreference);
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('focusin', onFocus);

    return {
        observe: (section) => {
            observer?.disconnect();
            animations.forEach((animation, element) => { if (!element.isConnected) animation.cancel(); });
            section?.querySelectorAll('[data-booster-offer]').forEach((card) => {
                if (!seen.has(card.dataset.boosterOffer)) observer?.observe(card);
            });
        },
        image: async (photo, next, animate = false) => {
            if (!photo || !next?.src) return;
            const current = images.get(photo);
            if (current?.src === next.src && current?.srcset === (next.srcset || '')) return;
            const state = { src: next.src, srcset: next.srcset || '' };
            images.set(photo, state);
            animations.get(photo)?.cancel();
            if (photo.getAttribute('src') === state.src && (photo.getAttribute('srcset') || '') === state.srcset) return;
            if (animate && enabled() && photo.animate) {
                const preview = new win.Image();
                preview.sizes = photo.sizes; preview.srcset = state.srcset; preview.src = state.src;
                try { await preview.decode(); } catch (_) { /* Native image loading remains the fallback. */ }
                if (images.get(photo) !== state || !photo.isConnected) return;
                await play(photo, [{ opacity: 1 }, { opacity: .15 }], 110);
            }
            if (images.get(photo) !== state || !photo.isConnected) return;
            photo.srcset = state.srcset; photo.src = state.src;
            if (animate) await play(photo, [{ opacity: .15 }, { opacity: 1 }], 300);
        },
        capture: () => new Map([...document.querySelectorAll('[data-booster-offer]')].map((card) => [card.dataset.boosterOffer, card.getBoundingClientRect()])),
        settle: async (before) => {
            if (!enabled()) return;
            // The rail restores its scroll position in the preceding animation frame callback.
            await new Promise((resolve) => {
                const frame = win.requestAnimationFrame(() => { win.clearTimeout(fallback); resolve(); });
                // A background tab may suspend animation frames while a purchase is completing.
                const fallback = win.setTimeout(() => { win.cancelAnimationFrame(frame); resolve(); }, 100);
            });
            if (!enabled()) return;
            await Promise.all([...document.querySelectorAll('[data-booster-offer]')].map((card, index) => {
                if (!visible(card)) return;
                const previous = before.get(card.dataset.boosterOffer);
                const next = card.getBoundingClientRect();
                seen.add(card.dataset.boosterOffer); observer?.unobserve(card);
                if (!previous) return enter(card, Math.min(index, 2));
                const x = previous.left - next.left;
                const y = previous.top - next.top;
                if (Math.abs(x) < 1 && Math.abs(y) < 1) return;
                return play(card, [{ transform: `translate3d(${x}px, ${y}px, 0)` }, { transform: 'translate3d(0, 0, 0)' }], 480);
            }));
        },
        confirm: async (card) => {
            // A brief, real success acknowledgement before replacing the purchased offer.
            if (!document.hidden) await new Promise((resolve) => win.setTimeout(resolve, 420));
            if (card?.isConnected) await play(card, [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-8px)' }], 180);
        },
        destroy: () => {
            observer?.disconnect(); stop();
            reduced.removeEventListener('change', onPreference);
            document.removeEventListener('visibilitychange', onVisibility);
            document.removeEventListener('focusin', onFocus);
        },
    };
};
